const post = {
  "slug": "checkout-dm-webhooks-for-orders-and-returns",
  "title": "Checkout DM Webhooks: Send Orders and Returns to Your System",
  "seoTitle": "Checkout DM Webhooks for Orders and Returns",
  "description": "Pro merchants can push order and return events to their own CRM or automation tool. Setup, the six events, signed payloads, retries and failed deliveries.",
  "excerpt": "Webhooks send an event to your own system the moment an order or return changes. Here is how to set them up, what each event carries and how to verify it.",
  "category": "Product",
  "tags": [
    "Checkout DM webhooks",
    "Order webhooks",
    "CRM integration",
    "Instagram order automation"
  ],
  "date": "2026-11-17",
  "cover": {
    "src": "/blog-images/checkout-dm-webhooks-for-orders-and-returns-cover.png",
    "alt": "Order and return events leaving Checkout DM as signed webhook messages"
  },
  "blocks": [
    {
      "type": "p",
      "text": "If you run a CRM, an ERP or an automation tool next to your Instagram shop, you do not want to copy orders across by hand. Webhooks push order and return events from Checkout DM to your own system the moment they happen, with full order, customer and item detail. There is no polling. Webhooks are a Pro plan feature."
    },
    {
      "type": "h2",
      "text": "Setting one up"
    },
    {
      "type": "ol",
      "items": [
        "**Open Webhooks in your dashboard** and add your endpoint URL. It must be an HTTPS address.",
        "**Pick the events you want.** You can choose any of the available events for each webhook.",
        "**Save the secret.** When the webhook is created, its secret is shown once. Copy it straight away, because you need it to verify incoming requests. Afterwards only the last four characters are shown."
      ]
    },
    {
      "type": "p",
      "text": "You can have up to 10 webhooks per account. Each can be switched off and back on, or removed, from the list. Removing one stops all events to that URL immediately."
    },
    {
      "type": "h2",
      "text": "The six events"
    },
    {
      "type": "table",
      "headers": [
        "Event",
        "When it fires"
      ],
      "rows": [
        [
          "order.created",
          "A new order is placed, before payment. There is no order number or status yet, because those are assigned once it is paid."
        ],
        [
          "order.paid",
          "Payment is confirmed, through Razorpay, Stripe, PayPal or manual UPI."
        ],
        [
          "order.shipped",
          "You mark the order as shipped from the dashboard. Includes the shipped time."
        ],
        [
          "return.requested",
          "A customer asks for a return, from the dashboard or Instagram DM."
        ],
        [
          "return.approved",
          "You approve a return request. Includes your note if you added one."
        ],
        [
          "return.rejected",
          "You reject a return request. Includes your note if you added one."
        ]
      ]
    },
    {
      "type": "h2",
      "text": "What a payload contains"
    },
    {
      "type": "p",
      "text": "Four fields are always present: the event name, your business ID, an entity ID and a timestamp. Everything else, such as order, customer, address and item detail, is included where it is relevant and available, so the exact fields vary by event. A few specifics are worth knowing before you write your integration:"
    },
    {
      "type": "ul",
      "items": [
        "**Amounts are strings, not numbers.** The total and the shipping fee are sent as strings to avoid rounding problems on money values. Item price is a plain number. Parse amounts explicitly.",
        "**Order number and status** are absent on order.created, because they are assigned right after payment. Status appears on order.paid only.",
        "**Customer email** may be missing on a return requested over Instagram DM, because email is not always captured in that flow. It is always present on order events.",
        "**Discount** is always present as a key, but its value is null when no coupon was applied.",
        "**Reason** appears only on the three return events, and **merchant note** only on return.approved and return.rejected.",
        "**Return items** reflect only the items being returned when that data is available, falling back to the full order\u2019s items otherwise."
      ]
    },
    {
      "type": "p",
      "text": "The practical advice is to write defensively: check that a field exists before reading it instead of assuming every field appears on every event."
    },
    {
      "type": "h2",
      "text": "What an order.paid payload looks like"
    },
    {
      "type": "p",
      "text": "To make this concrete, an order.paid event carries the four always-present fields plus the order details: an order number in the CDM-000044 style, the customer\u2019s name, phone and email, the delivery address, the total amount and currency, the status paid, the shipping fee, the discount (null if none) and a list of items with their names and prices. A return.approved event looks similar, with the reason the customer gave and your merchant note added, and it lists the items being returned. The webhooks guide shows both as real sample payloads, so you can build and test your receiving code against them."
    },
    {
      "type": "h2",
      "text": "Verifying signatures"
    },
    {
      "type": "p",
      "text": "Every request carries an X-CheckoutDM-Signature header. It is an HMAC-SHA256 hash of the raw request body, signed with your webhook secret. Always verify it before trusting a payload, and compute it from the raw bytes exactly as received. Even one extra space produces a different hash, so do not re-serialize the JSON first. The Checkout DM webhooks guide includes a worked example."
    },
    {
      "type": "h2",
      "text": "Retries and failed deliveries"
    },
    {
      "type": "p",
      "text": "If your endpoint does not answer with a 2xx status, Checkout DM retries automatically for a few attempts. After that, the delivery stops retrying on its own, but it is not lost. Open Webhooks in your dashboard and it is listed under Failed deliveries, grouped by the webhook it belongs to, with a one-click Resend once your endpoint is back up. Return a 2xx quickly, ideally within a few seconds, once you have accepted the event, and do any slow processing afterwards on your side."
    },
    {
      "type": "callout",
      "tone": "mint",
      "title": "Pairing webhooks with shipping",
      "text": "A common pattern is to use order.paid to create a record in your own system and order.shipped to update it. If you use a courier integration, see our guide to [auto-shipping Instagram orders with Shiprocket or Shippo](/blog/auto-ship-instagram-orders-shiprocket-shippo), and for the return side see [returns and exchanges in Instagram DMs](/blog/returns-and-exchanges-in-instagram-dms)."
    }
  ],
  "related": [
    {
      "to": "/webhooks-guide",
      "title": "Webhooks Guide",
      "description": "Events, payload fields, signing and retries in full."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "Webhooks are part of the Pro plan."
    },
    {
      "to": "/status-guide",
      "title": "Status Badges Guide",
      "description": "What each order and return status means."
    }
  ],
  "faqs": [
    {
      "q": "Which plan includes webhooks?",
      "a": "Webhooks are a Pro plan feature."
    },
    {
      "q": "How many webhooks can I create?",
      "a": "Up to 10 per account. Each one must use an HTTPS endpoint URL."
    },
    {
      "q": "Which events are available?",
      "a": "order.created, order.paid, order.shipped, return.requested, return.approved and return.rejected."
    },
    {
      "q": "How do I verify a webhook really came from Checkout DM?",
      "a": "Compute an HMAC-SHA256 of the raw request body using your webhook secret and compare it with the X-CheckoutDM-Signature header."
    },
    {
      "q": "Can I pause a webhook without deleting it?",
      "a": "Yes. Each webhook in your list can be switched off and back on, or removed entirely, which stops all events to that URL immediately."
    },
    {
      "q": "Are amounts sent as numbers?",
      "a": "No. The total and the shipping fee are sent as strings to avoid rounding problems, while the item price is a plain number. Parse amounts explicitly."
    },
    {
      "q": "What happens if my endpoint is down?",
      "a": "Checkout DM retries a few times, then stops. The delivery appears under Failed deliveries in your dashboard, where you can resend it once your endpoint is back."
    }
  ]
};

export default post;
